import React, { lazy } from 'react';
import { Navigate, Outlet, Route } from 'react-router-dom';
// Imported from the module, not the `features/auth` barrel: the barrel re-exports
// a `.jsx` file, which jest's transform does not cover.
import { ProtectedRoute } from '../auth/components/ProtectedRoute';
import { RequireActiveAccount } from '../../router/guards';
import { featureFlags } from '../../config/featureFlags';
import { SKILLS_PERMISSIONS } from './permissions';
import { NEW_SKILL_SEGMENT, SKILLS_ROUTE_SEGMENT, SKILL_LOGS_SEGMENT } from './constants';

// One lazy chunk per page, pointing at the page module instead of the feature
// barrel: importing this file from AppRouter must not pull the whole feature into
// the main bundle. The app's global <Suspense fallback={<RouteSkeleton />}> covers
// them.
const SkillsPage = lazy(() =>
  import('./pages/SkillsPage').then((module) => ({ default: module.SkillsPage })),
);
const SkillEditorPage = lazy(() =>
  import('./pages/SkillEditorPage').then((module) => ({ default: module.SkillEditorPage })),
);
const SkillLogsPage = lazy(() =>
  import('./pages/SkillLogsPage').then((module) => ({
    default: module.SkillLogsPage,
  })),
);
/**
 * Gated behind REACT_APP_FEATURE_SKILLS
 */
const SkillsSectionGate: React.FC = () => {
  if (!featureFlags.skills) return <Navigate to=".." replace />;
  return (
    <ProtectedRoute requiredPermission={SKILLS_PERMISSIONS.view}>
      <RequireActiveAccount>
        <Outlet />
      </RequireActiveAccount>
    </ProtectedRoute>
  );
};

/**
 * Route subtree of the Skills module, mounted by AppRouter under an agent
 * (`accounts/:accountId/assistants/:AssistantID`).
 *
 * A `Route` element and not a component: `<Routes>` only accepts `Route` children,
 * and a wrapper component would break routing silently.
 *
 * The parent route carries what every screen of the module shares — the `view`
 * permission and an active account — and each leaf adds only the permission that
 * differs. Nesting `ProtectedRoute` means both must pass, so someone who could
 * create but not view the section stays out, which is the intent.
 *
 * `new` is declared before `:skillId` so a skill id could never shadow it.
 */
export const skillsRoutes = (
  <Route path={SKILLS_ROUTE_SEGMENT} element={<SkillsSectionGate />}>
    {/*
      Secrets & variables (SL-1763) is NOT a route of its own — it's a third
      local tab inside `SkillsPage`, same URL as Skills/Executions. The reference
      prototype confirmed this: switching to that tab never changes the address
      bar. Its New secret / New variable / Replace value forms (SL-1764) are
      dialogs of that tab, so they have no route either.
    */}
    <Route index element={<SkillsPage />} />

    <Route
      path={NEW_SKILL_SEGMENT}
      element={
        <ProtectedRoute requiredPermission={SKILLS_PERMISSIONS.create}>
          <SkillEditorPage />
        </ProtectedRoute>
      }
    />

    <Route path=":skillId" element={<SkillEditorPage />} />

    <Route
      path={`:skillId/${SKILL_LOGS_SEGMENT}`}
      element={
        <ProtectedRoute requiredPermission={SKILLS_PERMISSIONS.viewLogs}>
          <SkillLogsPage />
        </ProtectedRoute>
      }
    />
  </Route>
);
